import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query,
  orderBy,
  serverTimestamp,
  where
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { uploadToCloudinary } from '../config/cloudinary';

// ============ PROGRAMS MANAGEMENT ============

export const createProgram = async (programData, flyerImage) => {
  try {
    let flyerUrl = null;

    // Upload flyer image to Cloudinary if provided
    if (flyerImage) {
      flyerUrl = await uploadToCloudinary(flyerImage);
    }

    const programDoc = await addDoc(collection(db, 'programs'), {
      title: programData.title,
      description: programData.description,
      flyerUrl: flyerUrl,
      createdBy: programData.createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'active',
      reactionsCount: 0,
      reactionsData: {
        like: 0,
        love: 0,
        care: 0,
        wow: 0,
        haha: 0,
        sad: 0,
        angry: 0,
      },
    });

    return { id: programDoc.id, ...programData, flyerUrl };
  } catch (error) {
    console.error('Error creating program:', error);
    throw error;
  }
};

export const updateProgram = async (programId, updates, newFlyerImage = null) => {
  try {
    let flyerUrl = null;

    if (newFlyerImage) {
      // Upload new flyer image to Cloudinary
      flyerUrl = await uploadToCloudinary(newFlyerImage);
      updates.flyerUrl = flyerUrl;
    }

    updates.updatedAt = serverTimestamp();

    await updateDoc(doc(db, 'programs', programId), updates);
    return { id: programId, ...updates };
  } catch (error) {
    console.error('Error updating program:', error);
    throw error;
  }
};

export const deleteProgram = async (programId) => {
  try {
    // Delete program document
    // Note: Cloudinary URLs are managed by Cloudinary, no need to delete them
    await deleteDoc(doc(db, 'programs', programId));
  } catch (error) {
    console.error('Error deleting program:', error);
    throw error;
  }
};

export const getAllPrograms = async () => {
  try {
    const q = query(
      collection(db, 'programs'),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const programs = [];
    querySnapshot.forEach((doc) => {
      programs.push({ id: doc.id, ...doc.data() });
    });
    return programs;
  } catch (error) {
    console.error('Error fetching programs:', error);
    throw error;
  }
};

export const getProgramById = async (programId) => {
  try {
    const docSnap = await getDoc(doc(db, 'programs', programId));
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error fetching program:', error);
    throw error;
  }
};

// ============ REACTIONS SYSTEM ============

export const addReaction = async (programId, userId, reactionType) => {
  try {
    const reactionsRef = collection(db, 'programs', programId, 'reactions');
    
    // Check if user already reacted
    const q = query(reactionsRef, where('userId', '==', userId));
    const existing = await getDocs(q);

    if (!existing.empty) {
      // Update existing reaction
      const existingDoc = existing.docs[0];
      await updateDoc(doc(db, 'programs', programId, 'reactions', existingDoc.id), {
        reactionType: reactionType,
        updatedAt: serverTimestamp(),
      });
    } else {
      // Add new reaction
      await addDoc(reactionsRef, {
        userId: userId,
        reactionType: reactionType,
        createdAt: serverTimestamp(),
      });
    }

    // Update reaction counts
    await updateReactionCount(programId);
  } catch (error) {
    console.error('Error adding reaction:', error);
    throw error;
  }
};

export const removeReaction = async (programId, userId) => {
  try {
    const reactionsRef = collection(db, 'programs', programId, 'reactions');
    const q = query(reactionsRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      await deleteDoc(querySnapshot.docs[0].ref);
      // Update reaction counts
      await updateReactionCount(programId);
    }
  } catch (error) {
    console.error('Error removing reaction:', error);
    throw error;
  }
};

export const updateReactionCount = async (programId) => {
  try {
    const reactionsRef = collection(db, 'programs', programId, 'reactions');
    const allReactions = await getDocs(reactionsRef);

    const counts = {
      like: 0,
      love: 0,
      care: 0,
      wow: 0,
      haha: 0,
      sad: 0,
      angry: 0,
    };

    allReactions.forEach((doc) => {
      const reactionType = doc.data().reactionType;
      if (counts.hasOwnProperty(reactionType)) {
        counts[reactionType]++;
      }
    });

    await updateDoc(doc(db, 'programs', programId), {
      reactionsData: counts,
      reactionsCount: allReactions.size,
    });
  } catch (error) {
    console.error('Error updating reaction count:', error);
    throw error;
  }
};

export const getUserReaction = async (programId, userId) => {
  try {
    const q = query(
      collection(db, 'programs', programId, 'reactions'),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      return querySnapshot.docs[0].data().reactionType;
    }
    return null;
  } catch (error) {
    console.error('Error fetching user reaction:', error);
    throw error;
  }
};

// ============ ALERTS SYSTEM ============

export const sendAlertToAllUsers = async (alertData) => {
  try {
    // Get all users
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);

    const alertId = Date.now();

    // Create alert in alerts collection
    const alertDoc = await addDoc(collection(db, 'alerts'), {
      title: alertData.title,
      message: alertData.message,
      type: alertData.type || 'info', // 'info', 'warning', 'success', 'urgent'
      createdBy: alertData.createdBy,
      createdAt: serverTimestamp(),
      readBy: [],
      recipientCount: usersSnapshot.size,
      sendEmail: alertData.sendEmail || false,
    });

    // Add alert to each user's collection
    const promises = usersSnapshot.docs.map((userDoc) =>
      addDoc(collection(db, 'users', userDoc.id, 'notifications'), {
        alertId: alertDoc.id,
        title: alertData.title,
        message: alertData.message,
        type: alertData.type || 'info',
        read: false,
        createdAt: serverTimestamp(),
        ...(alertData.sendEmail && { email: userDoc.data().email }),
      })
    );

    await Promise.all(promises);

    return { id: alertDoc.id, ...alertData };
  } catch (error) {
    console.error('Error sending alert:', error);
    throw error;
  }
};

export const markAlertAsRead = async (userId, notificationId) => {
  try {
    await updateDoc(
      doc(db, 'users', userId, 'notifications', notificationId),
      { read: true }
    );
  } catch (error) {
    console.error('Error marking alert as read:', error);
    throw error;
  }
};

export const getUserNotifications = async (userId) => {
  try {
    const q = query(
      collection(db, 'users', userId, 'notifications'),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const notifications = [];
    querySnapshot.forEach((doc) => {
      notifications.push({ id: doc.id, ...doc.data() });
    });
    return notifications;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

export const deleteNotification = async (userId, notificationId) => {
  try {
    await deleteDoc(doc(db, 'users', userId, 'notifications', notificationId));
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};
